import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useDebounceValue } from 'usehooks-ts';
import { toast } from 'sonner';

import { useQuantaReferralCheck } from '@/hooks/api/quanta/useQuantaReferralCheck';
import { useQuantaReferralSet } from '@/hooks/api/quanta/useQuantaReferralSet';
import { Button } from '@/components/button';
import { cn } from '@/utils/cn';

import { generateReferralCode, getReferralCodeError, type ReferralCodeError } from '../validation.utils';

import CopyReferral from './CopyReferral';
import MissionTaskCardView from './MissionTaskCardView';
import QuantaPoints from './QuantaPoints';

interface InviteFriendsProps {
  referred: boolean;
  referrer: string | null;
  points: number;
  close: VoidFunction;
}

const InviteFriends: React.FC<InviteFriendsProps> = ({ referred, referrer, points, close }: InviteFriendsProps) => {
  const { mutate: referralCheckMutate, isError: isReferralCheckError /*, isPending: isReferralCheckPending */ } = useQuantaReferralCheck();
  const { mutate: referralSetMutate, isPending: isReferralSetPending } = useQuantaReferralSet({
    onSuccess: () => {
      close();
    },
    onError: (error) => {
      toast.error('Could not set referral code.');
      console.error(error);
    },
  });

  const [referralCode, setReferralCode] = useState<string>(() => generateReferralCode());
  const [validationError, setValidationError] = useState<ReferralCodeError | null>(null);
  const [debouncedValue] = useDebounceValue(referralCode, 500);

  useEffect(() => {
    if (debouncedValue && getReferralCodeError(debouncedValue) === null) {
      referralCheckMutate(debouncedValue);
    }
  }, [debouncedValue, referralCheckMutate]);

  const handleReferralCodeChange = (value: string) => {
    setReferralCode(value);
    setValidationError(getReferralCodeError(value));
  };

  return (
    <MissionTaskCardView>
      <div
        className={cn(
          'relative flex h-full sm:h-[calc(100%-200px)] sm:max-h-[850px] max-w-2xl bg-surface-primary border border-border-primary sm:rounded-2xl overflow-hidden',
          referred ? 'min-h-[640px] md:min-h-[654px]' : 'min-h-[565px] md:min-h-[580px]',
        )}
      >
        <div className='flex flex-1 flex-col gap-4 md:gap-6 lg:gap-10 p-6 md:p-10 z-10'>
          <p className='text-2xl md:text-[32px] font-bold text-text-primary self-center'>Invite friends to Quanta</p>
          <div className='flex flex-col gap-4 md:gap-6'>
            {referred && (
              <p className='text-lg md:text-xl text-text-primary'>
                Welcome aboard — you&apos;ve already earned{' '}
                <span className='inline-block'>
                  <QuantaPoints isPlus={false} points={points} />
                </span>{' '}
                thanks to the whitelisted referral from <span className='text-blue-100'>{referrer}</span> code!
              </p>
            )}
            <p className='text-lg md:text-xl text-text-primary'>
              Share your own referral code/link to invite friends and keep earning — you can even customize it with your name to make it
              yours.
            </p>
            <div className='flex flex-col gap-4'>
              <CopyReferral
                title='Referral code'
                value={referralCode}
                onChange={handleReferralCodeChange}
                isReferralCheckError={isReferralCheckError}
                validationError={validationError}
              />
              <CopyReferral title='Referral link' value={`https://app.ducatprotocol.com/quanta?referral=${referralCode}`} />
            </div>
            <div className='flex rounded-2xl'>
              <div className='flex flex-1 rounded-2xl overflow-hidden'>
                <div className='flex flex-1 items-center gap-6 py-3 md:py-4 px-4 md:px-8 bg-surface-secondary'>
                  <div className='flex flex-1 items-center justify-between gap-6'>
                    <p className='text-lg md:text-xl text-text-primary leading-none'>You receive per referral</p>
                    <QuantaPoints points={250} />
                  </div>
                </div>
              </div>
            </div>
            <div className='flex justify-center'>
              <Button
                variant='primary'
                disabled={!!validationError || isReferralCheckError || isReferralSetPending /* || isReferralCheckPending */}
                onClick={() => referralSetMutate(referralCode)}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
        <Image
          src='/assets/quanta/invite.png'
          alt='Invite friends'
          width={337}
          height={337}
          className='absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none'
        />
      </div>
    </MissionTaskCardView>
  );
};

export default InviteFriends;
